import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { CopilotState, Message } from './types';

const initialState: CopilotState = {
  isLoading: false,
  messages: [],
  showMenu: false,
  toastText: '',
  showToast: false,
  newSession: false,
  isTyping: false,
  replies: [],
  sampleQuestions: [],
  socket: null,
  isCopilotOpen: true,
  retry: 0,
  clientId: '',
  threadId: '',
  isRecording: false,
  agentAI: false,
  agentAIContext: null,
  status: null,
  uploadedFile: null,
  workflowMetaData: {},
  summariseData: '',
  lastUpdated: null,
};

export const copilotSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setIsLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setIsTyping(state, action: PayloadAction<boolean>) {
      state.isTyping = action.payload;
    },
    setSampleQuestions(state, action: PayloadAction<Array<string>>) {
      state.sampleQuestions = action.payload;
    },
    setReplies(state, action: PayloadAction<Array<string>>) {
      state.replies = action.payload;
    },
    setShowMenu(state, action: PayloadAction<boolean>) {
      state.showMenu = action.payload;
    },
    setShowToast(state, action: PayloadAction<boolean>) {
      state.showToast = action.payload;
    },
    setToastText(state, action: PayloadAction<string>) {
      state.toastText = action.payload;
    },
    setStatus(
      state,
      action: PayloadAction<{
        type: 'error' | 'success' | 'info';
        message: string;
        duration?: number;
      }>,
    ) {
      state.status = action.payload;
    },
    setNewSession(state, action: PayloadAction<boolean>) {
      state.newSession = action.payload;
    },
    setMessages(state, action: PayloadAction<Array<Message>>) {
      state.messages = action.payload;
    },
    addMessage: (state, action: PayloadAction<Message>) => {
      state.messages = [...state.messages, action.payload];
    },
    setSocket: (state, action: PayloadAction<WebSocket | null>) => {
      state.socket = action.payload;
    },
    setIsCopilotOpen(state, action: PayloadAction<boolean>) {
      state.isCopilotOpen = action.payload;
    },
    setRetryCount(state, action: PayloadAction<number>) {
      state.retry = action.payload;
    },
    setClientId(state, action: PayloadAction<string>) {
      state.clientId = action.payload;
    },
    setThreadId(state, action: PayloadAction<string>) {
      state.threadId = action.payload;
    },
    setUploadedFile(state, action: PayloadAction<File | null>) {
      state.uploadedFile = action.payload;
    },
    toggleRecording(state) {
      state.isRecording = !state.isRecording;
    },
    toggleAgentAI(state, action: PayloadAction<any>) {
      state.agentAI = !state.agentAI;
      state.agentAIContext = action?.payload ?? null;
    },
    setWorkflowMetaData(state, action: PayloadAction<any>) {
      state.workflowMetaData = {
        ...(state.workflowMetaData || {}),
        ...action.payload,
      };
    },
    setSummariseData(state, action: PayloadAction<string>) {
      state.summariseData = action.payload;
    },
    setLastUpdated(state, action: PayloadAction<number | null>) {
      state.lastUpdated = action.payload;
    },
    clearStore(state) {
      state = initialState;
      return state;
    },
  },
});

export const {
  setClientId,
  setThreadId,
  setIsLoading,
  setMessages,
  setShowMenu,
  setShowToast,
  setToastText,
  addMessage,
  setNewSession,
  setIsTyping,
  setReplies,
  setSampleQuestions,
  setSocket,
  setIsCopilotOpen,
  setRetryCount,
  toggleRecording,
  toggleAgentAI,
  setStatus,
  clearStore,
  setUploadedFile,
  setWorkflowMetaData,
  setSummariseData,
  setLastUpdated,
} = copilotSlice.actions;

const reducer = { copilot: copilotSlice.reducer };

export default reducer;
